import type { ErrorInfo, ReactNode } from 'react';
import React, { Component } from 'react';
import { APP_ERROR_OUTCOME, reportClientError } from '@highlight-cp/metrics/client';

import ServerError from './ServerError';
import ThemeShell from './ThemeShell';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  hasRetried: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    hasRetried: false,
  };

  static getDerivedStateFromError(_: Error): Partial<State> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    reportClientError({
      outcome: APP_ERROR_OUTCOME.REACT_BOUNDARY,
      message: error.message,
      errorName: error.name,
    });
  }

  // The first Try again renders the app again, which keeps its state. Every later one, for the rest
  // of the session, reloads the page. The flag isn't cleared after a retry that renders: a child
  // that crashes in an effect would then re-render and crash on every Try again, never reloading.
  private handleRetry = () => {
    if (this.state.hasRetried) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, hasRetried: true });
  };

  render() {
    if (this.state.hasError) {
      // This boundary sits above Layout, so the fallback brings its own theme for core-ui.
      return (
        <ThemeShell>
          <ServerError onRetry={this.handleRetry} />
        </ThemeShell>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
